import { Box, Stack, TableCell, Typography } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import {
  getPremiumDetails,
  isAiAddonLocked,
  sanitizeURL,
  timeConverter
} from 'src/common/config/FunctionHelper';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { getPreviousScan } from 'src/common/Scraper';
import { scannedPageSx } from 'src/content/scannedPages/scannedPageSx';
import ScoreProgress from '../WebsiteAuditDashboardcomponentes/ScoreProgressList';
import { pdfContainerSx } from 'src/common/PdfComponents/PdfSxs';
import { removeNegativeSign } from 'src/common/config/FunctionHelperPartTwo';

/**
 * RowPdf component renders a row in a PDF, displaying site audit data.
 *
 * @param {number} index - The index of the row.
 * @param {object} row - The data for the row.
 * @param {object} subscriptionDetails - Details of the user's subscription.
 * @param {string} listViewUrl - The URL for the list view.
 * @param {boolean} indiVidualScan - Indicates whether it's an individual scan.
 */
const RowPdf = ({
  index,
  row,
  subscriptionDetails,
  listViewUrl,
  indiVidualScan,
  reportMetaData
}: {
  index: any;
  row: any;
  subscriptionDetails: any;
  listViewUrl: any;
  indiVidualScan: any;
  reportMetaData?: any;
}) => {
  // State to control the row's visibility
  const [open, setOpen] = useState(true);

  // State to store previous scan data
  const [previousScanData, setPreviousScanData] = useState([]);

  // Get the site ID from the Redux store
  const siteId =
    useSelector((state: RootState) => state?.siteAudit?.siteId) ?? 0;

  // Check if the user is a premium user
  let isPremiumUser = useSelector(
    (state: RootState) => state?.user?.premiumUser
  );
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isFreePlan = activePlanName === planDetails.free;
  const isNotUnlocked = isAiAddonLocked();
  let userDetails: any = useSelector(
    (state: RootState) => state?.counter?.userDetails
  );
  /**
   * Returns the formatted site URL (hostname + pathname).
   *
   * @param {string} urlLink - The URL to be formatted.
   * @returns {string} - Formatted URL.
   */
  const siteUrlFormat = (urlLink: string) => {
    const fullUrl = new URL(urlLink);
    let url = fullUrl.hostname + fullUrl.pathname;
    let lastChart = url.slice(-1);
    if (lastChart === '/') {
      url = url.slice(0, -1);
    }
    return url;
  };

  useEffect(() => {
    // Fetch and handle previous data for the row based on the site ID
    // handlePreviousData(row?._id);
    setPreviousScanData(row?.allPreviousScan);
    setOpen(true);
  }, [siteId]);

  /**
   * Calculates and returns the comparison score based on previous and current data.
   *
   * @param {object} previousData - Data from the previous scan.
   * @param {number} currentOptimize - Current optimization count.
   * @param {number} currentTotal - Total count for the current scan.
   * @returns {JSX.Element} - JSX element displaying the comparison score.
   */
  const getComparisonScore = (previousData, currentOptimize, currentTotal) => {
    let currentScanScore =
      (Number(currentOptimize) / Number(currentTotal)) * 100 || 0;

    let totalChecks, optimizedChecks;

    if (
      previousData?.optimized_scan.length &&
      previousData?.optimized_scan[0]?.newChecksWeight !== undefined
    ) {
      // Use newChecksWeight and newPassChecksWeight if they exist
      totalChecks =
        previousData?.optimized_scan.reduce(
          (sum, item) => sum + (item.newChecksWeight || 0),
          0
        ) || 0;

      optimizedChecks =
        previousData?.optimized_scan.reduce(
          (sum, item) => sum + (item.newPassChecksWeight || 0),
          0
        ) || 0;
    } else {
      // Fallback to previous logic if the keys are not present
      totalChecks = previousData?.optimized_scan.length;

      optimizedChecks = previousData?.optimized_scan?.filter((data) => {
        if (data?.suggestions?.length === 0) {
          return data;
        }
      }).length;
    }

    let previousScanScore =
      (Number(optimizedChecks) / Number(totalChecks)) * 100 || 0;
    const comparisonScore = currentScanScore - previousScanScore;
    const score = Number(
      removeNegativeSign(Math.round(Number(comparisonScore)))
    );

    // Check if score is not a valid number or rounds to 0
    if (isNaN(score) || score === 0) {
      return <></>; // Don't render anything
    }
    return getCompareScoreJSX(comparisonScore);
  };

  /**
   * Returns JSX element displaying the comparison score based on the comparison score value.
   *
   * @param {number} comparisonScore - The calculated comparison score.
   * @returns {JSX.Element} - JSX element displaying the comparison score.
   */
  const getCompareScoreJSX = (comparisonScore) => {
    if (Math.sign(comparisonScore) === 1) {
      return (
        <Box className="flex items-center ">
          {/*<MovingIcon sx={{ color: 'var(--color-secondary)', mr: 1 }} />*/}
          <div
            className="ml-3 mr-3"
            style={{
              ...pdfContainerSx?.commonImgIconStyle,
              height: 'auto'
            }}
          >
            <img
              src="/static/images/icons/green_up.svg"
              alt="icon"
              style={{}}
            />
          </div>

          <Box>{removeNegativeSign(comparisonScore)}%</Box>
        </Box>
      );
    } else if (Math.sign(comparisonScore) === -1) {
      return (
        <Box className="flex items-center">
          <img
            className="ml-3 mr-3"
            src="/static/images/icons/red_down.svg"
            alt="icon"
            style={{
              ...pdfContainerSx?.commonImgIconStyle,
              objectFit: 'contain'
            }}
          />
          <Box>{removeNegativeSign(comparisonScore)}%</Box>
        </Box>
      );
    }
  };

  /**
   * Handles fetching and storing the previous scan data for the specified sitePageId.
   *
   * @param {string} sitePageId - The ID of the site page to fetch previous scan data for.
   */
  // handle get previous data
  const handlePreviousData = async (sitePageId) => {
    const getPreviousResponse: any = await getPreviousScan({
      sitePageId: sitePageId
    });
    if (getPreviousResponse?.status == 'success') {
      setPreviousScanData(getPreviousResponse?.response);
    }
  };

  return (
    <Fragment>
      <tr>
        <TableCell
          sx={{
            marginBottom: '15px !important',
            width: '35%',
            fontWeight: 400
          }}
        >
          <Typography sx={scannedPageSx?.urlSx}>
            {sanitizeURL(siteUrlFormat(row?.url))}
          </Typography>
        </TableCell>
        <td
          style={{
            width: '16rem',
            maxWidth: '16rem',
            textAlign: 'left',
            marginBottom: '10px',
            wordWrap: 'break-word'
          }}
        >
          <Box className="flex items-center">
            <Stack>
              <ScoreProgress score={Number(row?.score) || 0} />
            </Stack>
            <Stack style={{}}>
              {getCompareScoreJSX(row?.changeSinceLastScan)}
            </Stack>
          </Box>
        </td>
        <td
          style={{
            width: '16rem',
            maxWidth: '16rem',
            textAlign: 'left',
            marginBottom: '10px',
            wordWrap: 'break-word'
          }}
        >
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '18px',
              textAlign: 'left',
              color: '#3B11B8'
            }}
          >
            {Object.keys(subscriptionDetails)?.length !== 0 &&
            isFreePlan &&
            isNotUnlocked
              ? 'test'
              : row?.scan?.keyword
              ? row?.scan?.keyword
              : '-'}
          </p>
        </td>
        <td
          style={{
            width: '12rem',
            maxWidth: '12rem',
            textAlign: 'left',
            marginBottom: '10px',
            wordWrap: 'break-word',
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-heading)'
          }}
        >
          {Object.keys(subscriptionDetails)?.length !== 0 &&
          isFreePlan &&
          isNotUnlocked
            ? 'test'
            : getPremiumDetails(row?.scan?.premium_scan, 'Keyword Volume')}
        </td>
        <td
          style={{
            width: '16rem',
            maxWidth: '16rem',
            textAlign: 'left',
            marginBottom: '10px',
            wordWrap: 'break-word'
          }}
        >
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 'var(--line-height-18)',
              textAlign: 'left',
              color: 'var(--color-text-tertiary)'
            }}
          >
            {timeConverter(
              row?.scan?.created_at,
              userDetails?.date_format ||
                reportMetaData?.dateFormat ||
                'DD/MM/YYYY'
            )}
          </p>
        </td>
      </tr>
      {previousScanData?.length !== 0 &&
        previousScanData?.map((row, index) => (
          <tr key={index}>
            {/** WebSite URL*/}
            <td
              style={{
                width: '15rem',
                //minWidth: '15rem',
                maxWidth: '15rem',
                textAlign: 'left',
                marginBottom: '10px',
                wordWrap: 'break-word'
              }}
            >
              {''}
            </td>
            <td
              style={{
                width: '13rem',
                //minWidth: '13rem',
                maxWidth: '13rem',
                textAlign: 'left',
                marginBottom: '10px',
                wordWrap: 'break-word'
              }}
            >
              <Box className="flex items-center">
                {' '}
                <Stack>
                  <ScoreProgress score={Number(row?.score) || 0} />
                </Stack>
                <Stack style={{}}>
                  {getCompareScoreJSX(row?.changeSinceLastScan)}
                </Stack>
              </Box>
            </td>
            <td
              style={{
                width: '10rem',
                //minWidth: '10rem',
                maxWidth: '10rem',
                textAlign: 'left',
                marginBottom: '10px',
                wordWrap: 'break-word'
              }}
            >
              <p
                style={{
                  fontFamily: 'Inter',
                  fontSize: 'var(--font-size-14)',
                  fontWeight: 400,
                  lineHeight: 'var(--line-height-18)',
                  textAlign: 'left',
                  color: '#3B11B8'
                }}
              >
                {Object.keys(subscriptionDetails)?.length !== 0 &&
                isFreePlan &&
                isNotUnlocked
                  ? 'test'
                  : row?.keyword
                  ? row?.keyword
                  : '-'}
              </p>
            </td>
            <td
              style={{
                width: '3rem',
                minWidth: '3rem',
                maxWidth: '3rem',
                textAlign: 'left',
                marginBottom: '10px',
                wordWrap: 'break-word',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-text-heading)'
              }}
            >
              {Object.keys(subscriptionDetails)?.length !== 0 &&
              isFreePlan &&
              isNotUnlocked
                ? 'test'
                : row?.keyVolume
                ? row?.keyVolume
                : '-'}
            </td>
            <td
              style={{
                width: '13rem',
                //minWidth: '13rem',
                maxWidth: '13rem',
                textAlign: 'left',
                marginBottom: '10px',
                wordWrap: 'break-word'
              }}
            >
              <p
                style={{
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: 'var(--line-height-18)',
                  textAlign: 'left',
                  color: 'var(--color-text-tertiary)'
                }}
              >
                {timeConverter(
                  row?.date,
                  userDetails?.date_format ||
                    reportMetaData?.dateFormat ||
                    'DD/MM/YYYY'
                )}
              </p>
            </td>
          </tr>
        ))}
    </Fragment>
  );
};

export default RowPdf;
